import React, { useContext, useEffect, useState } from "react";
import OwTitle from "../OwTitle/OwTitle";
import OwButton from "../OwButton/OwButton";
import OwLabel from "../OwLabel/OwLabel";
import OwSortHeader from "../OwSortHeader/OwSortHeader";
import UIService from "../../../services/UIService";
import * as _ from "lodash-es";
import OwTranslate from "../OwTranslate/OwTranslate";
import SupplierContext from "../../../contexts/SupplierContext";
import UserContext from "../../../contexts/UserContext";
import IOnGetOrdersPickListDTO from "../../../models/DTO/IOnGetOrdersPickListDTO";
import SortOrderEnum from "../../../models/SortOrderEnum";
import moment from "moment";
import IGetOrdersPickListDTO from "../../../models/DTO/IGetOrdersPickListDTO";
import EndpointsEnum from "../../../models/EndpointsEnum";
import queryString from "query-string";
import { useLocation, useNavigate } from "react-router-dom";
import "./SimplePickList.scss";
import PickListComponentEnum from "../../../models/PickListComponentEnum";
import { PreformPickList } from "../../custom/PreformPickList/PreformPickList";
import Breadcrumbs from "../Breadcrumbs/Breadcrumbs";
import OwPrintButton from "../OwPrintButton/OwPrintButton";
import IBreadcrumbs from "../../../models/IBreadcrumbs";
import UiContext from "../../../contexts/UiContext";
import { SocketContext } from "../../../contexts/SocketContext";
import { Parser } from "@json2csv/plainjs";
import { IOption } from "../../../models/IOption";
import OwCheckbox from "../OwCheckbox/OwCheckbox";
import OwSelectOption from "../OwSelectOption/OwSelectOption";
import { useDailyRouteGroups } from "../../../hooks/useDailyRouteGroups";
import { DailyRouteCoverSheet } from "../DailyRouteCoverSheet/DailyRouteCoverSheet";

type Props = {};

export const SimplePickList: React.FC<Props> = () => {
    const userModel = useContext(UserContext);
    const supplierModel = useContext(SupplierContext);
    const location = useLocation();
    const socket = useContext(SocketContext)!;
    const uiContext = useContext(UiContext);
    const navigate = useNavigate();

    const isFromDailyRoutes = location.pathname.includes("routes/daily");
    const { dailyRouteGroups, loading: dailyRoutesLoading, hasDailyRoutes } = useDailyRouteGroups();

    const getNavItems = (): IBreadcrumbs[] => {
        const items: IBreadcrumbs[] = [];

        if (isFromDailyRoutes) {
            items.push({ title: "daily_routes", url: "/routes/daily" });
        } else {
            items.push({ title: "dashboard", url: "/dashboard" });
            items.push({ title: "orders", url: "/orders" });
        }

        items.push({ title: "pick_list", url: "" });

        return items;
    };

    const [pickListData, setPickListData] = useState<IOnGetOrdersPickListDTO>();
    const [groupedPickListData, setGroupedPickListData] = useState<Map<string, IOnGetOrdersPickListDTO>>(new Map());
    const [sortOrder, setSortOrder] = useState<SortOrderEnum>(SortOrderEnum.DESC);
    const [sortCol, setSortCol] = useState<string>("updated_at");
    const [navItems, setNavItems] = useState(getNavItems());
    const [template, setTemplate] = useState<"STANDARD" | "MINIMAL">("STANDARD");
    const [printOrders, setPrintOrders] = useState(true);

    const packingListTemplates: IOption[] = [
        { value: "STANDARD", label: "8.5x11" },
        { value: "MINIMAL", label: "4x6" },
    ];

    useEffect(() => {
        if (!hasDailyRoutes) {
            socket.on(EndpointsEnum.OrderOnGetOrdersPickList, onGetOrdersPickList);
            getPickList();
        }

        return () => {
            socket.off(EndpointsEnum.OrderOnGetOrdersPickList, onGetOrdersPickList);
        };
    }, []);

    // Sequential socket calls per daily route group
    useEffect(() => {
        if (!hasDailyRoutes || dailyRoutesLoading || dailyRouteGroups.length === 0) return;

        let currentGroupIndex = 0;
        const results = new Map<string, IOnGetOrdersPickListDTO>();

        const handleGroupResponse = (data: IOnGetOrdersPickListDTO) => {
            const group = dailyRouteGroups[currentGroupIndex];
            results.set(group.dailyRoute.guid, data);
            currentGroupIndex++;

            if (currentGroupIndex < dailyRouteGroups.length) {
                emitPickListForGroup(dailyRouteGroups[currentGroupIndex]);
            } else {
                socket.off(EndpointsEnum.OrderOnGetOrdersPickList, handleGroupResponse);
                setGroupedPickListData(new Map(results));
            }
        };

        const emitPickListForGroup = (group: { orderGuids: string[] }) => {
            const pickListOrders = group.orderGuids.map((guid) => ({ guid }));
            const payload: IGetOrdersPickListDTO = {
                userGuid: userModel.guid,
                userToken: userModel.token,
                supplierGuid: supplierModel!.guid,
                orders: pickListOrders,
            };

            if (supplierModel.pick_list_component === PickListComponentEnum.PREFORM) {
                payload.dontGroupItems = true;
            }

            socket.emit(EndpointsEnum.OrderGetOrdersPickList, payload);
        };

        socket.on(EndpointsEnum.OrderOnGetOrdersPickList, handleGroupResponse);
        emitPickListForGroup(dailyRouteGroups[0]);

        return () => {
            socket.off(EndpointsEnum.OrderOnGetOrdersPickList, handleGroupResponse);
        };
    }, [hasDailyRoutes, dailyRoutesLoading, dailyRouteGroups]);

    const getOrderGuids = () => {
        const queryStringValues = queryString.parse(location.search);

        const qsOrders = queryStringValues.orders;

        if (_.isString(qsOrders)) {
            return qsOrders?.split(",");
        } else {
            return [];
        }
    };

    const getPickList = () => {
        const orderGuidStrings = getOrderGuids();

        const pickListOrders: { guid: string }[] = [];

        for (const str of orderGuidStrings) {
            pickListOrders.push({ guid: str });
        }

        const payload: IGetOrdersPickListDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel!.guid,
            orders: pickListOrders,
        };

        if (supplierModel.pick_list_component) {
            switch (supplierModel.pick_list_component) {
                case PickListComponentEnum.PREFORM:
                    payload.dontGroupItems = true;
                    break;
            }
        }

        socket.emit(EndpointsEnum.OrderGetOrdersPickList, payload);
    };

    const onGetOrdersPickList = (data: IOnGetOrdersPickListDTO) => {
        setPickListData(data);
    };

    let tableTheme = "";

    if (!UIService.isMainViewLight()) {
        tableTheme = "table-dark text-white";
    }

    const buildCsvRowsFromPickListData = (data: IOnGetOrdersPickListDTO) => {
        const pickItemsArr =
            data.orderItems.map((item) => {
                let pickTotalQty = 0;

                let total_qty = Number(item.total_qty);

                if (supplierModel && supplierModel.has_order_bonus) {
                    total_qty += Number(item.total_bonus_qty_frozen);
                }

                pickTotalQty += total_qty;

                let description = item.Catalog_Item?.description || "";
                let name = item.Catalog_Item?.name || "";
                let sku = item.Catalog_Item?.sku || "";

                // We have to replace any pound symbols, otherwise it'll break the download data when
                // outputing to CSV file.
                if (description.indexOf("#") >= 0) {
                    description = description.replaceAll("#", "No.");
                }

                if (name.indexOf("#") >= 0) {
                    name = name.replaceAll("#", "No.");
                }

                if (sku.indexOf("#") >= 0) {
                    sku = sku.replaceAll("#", "No.");
                }

                return {
                    Qty: `${pickTotalQty}`,
                    SKU: sku,
                    Name: name,
                    Description: description,
                    "Qty. Picked": "",
                };
            }) || [];

        const orders =
            data.orders.map((order) => {
                let vendor = order.vendor || "";

                if (vendor.indexOf("#") >= 0) {
                    vendor = vendor.replaceAll("#", "No.");
                }
                return { Qty: `${order.name} (${vendor})`, SKU: "", Name: "", Description: "", "Qty. Picked": "" };
            }) || [];

        return [...pickItemsArr, ...orders];
    };

    const exportPickListCSV = () => {
        const headerRow = ["Qty", "SKU", "Name", "Description", "Qty. Picked"];

        let rows: any[] = [];

        if (hasDailyRoutes && groupedPickListData.size > 0) {
            for (const [, data] of groupedPickListData) {
                rows = [...rows, ...buildCsvRowsFromPickListData(data)];
            }
        } else if (pickListData) {
            rows = buildCsvRowsFromPickListData(pickListData);
        }

        const parser = new Parser({ fields: headerRow });
        const csvContent = parser.parse(rows);

        const yearMonthDay = moment().format("YYYY-MM-DD");

        const encodedUri = encodeURI("data:text/csv;charset=utf-8," + csvContent);
        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", `${yearMonthDay}-Pick-List.csv`);

        link.click();
    };

    const sortPickList = (newSortCol: string, newSortOrder: SortOrderEnum) => {
        let orderItems = _.sortBy(
            pickListData!.orderItems,
            (item) => {
                // @ts-expect-error
                return item.Catalog_Item[sortCol];
            },
            newSortOrder
        );

        if (pickListData && orderItems) {
            if (sortOrder === SortOrderEnum.DESC) {
                orderItems = orderItems.reverse();
            }

            pickListData.orderItems = orderItems;

            setSortOrder(newSortOrder);
            setSortCol(newSortCol);
            setPickListData(pickListData);
        }
    };

    const ordersDetail = _.map(pickListData?.orders, (order) => {
        return (
            <div key={order.guid} className={"col-3"}>
                <i className={"far fa-square"} /> {order?.name} ({order?.vendor})
            </div>
        );
    });

    const orders = (
        <div className={printOrders ? "" : "d-print-none"}>
            <div className={"d-print-none mt-4"}>
                <OwCheckbox
                    fn={() => {
                        setPrintOrders(!printOrders);
                    }}
                    checked={printOrders}
                    label={"print_orders_detail"}
                />
            </div>
            <OwTitle className={"mt-3"} title={"orders"} tag={"h5"} />
            <div className={"row d-flex"}>{ordersDetail}</div>
        </div>
    );

    let pickTotalQty = 0;

    const tbodyRows = _.map(pickListData?.orderItems, (item) => {
        let extendedDetails: any = null;

        if (item.note) {
            extendedDetails = (
                <tr>
                    <td></td>
                    <td colSpan={3}>
                        <b>
                            <OwTranslate defaultMessage={"note"} id={"note"} />:
                        </b>{" "}
                        {item.note}
                    </td>
                </tr>
            );
        }

        let totalQty = Number(item.total_qty);

        if (supplierModel && supplierModel.has_order_bonus) {
            totalQty += Number(item.total_bonus_qty_frozen);
        }

        pickTotalQty += totalQty;

        return (
            <tbody>
                <tr key={item.guid}>
                    <td>{totalQty}</td>
                    <td>{item.Catalog_Item?.sku}</td>
                    <td>{item.Catalog_Item?.name}</td>
                    <td>{item.Catalog_Item?.description}</td>
                    <td>
                        {template === "STANDARD" ? (
                            <div>
                                <OwLabel title={"qty_picked"} />:
                            </div>
                        ) : null}
                    </td>
                </tr>
                {extendedDetails}
            </tbody>
        );
    });

    const selectTemplate = (option: IOption) => {
        setTemplate(option.value as "STANDARD" | "MINIMAL");
    };

    const viewPackingLists = () => {
        if (hasDailyRoutes) {
            const params = new URLSearchParams(location.search);
            const drParam = params.get("dailyRoutes") || "";
            navigate(`/routes/daily/packing_list?dailyRoutes=${drParam}`);
        } else {
            const orderGuidStrings = getOrderGuids();
            const basePath = isFromDailyRoutes ? "/routes/daily" : "/orders";
            navigate(`${basePath}/packing_list?orders=${orderGuidStrings?.join(",")}`);
        }
    };

    const renderPickListTable = (data: IOnGetOrdersPickListDTO) => {
        let groupTotalQty = 0;

        const groupTbodyRows = _.map(data.orderItems, (item) => {
            let extendedDetails: any = null;

            if (item.note) {
                extendedDetails = (
                    <tr>
                        <td></td>
                        <td colSpan={3}>
                            <b>
                                <OwTranslate defaultMessage={"note"} id={"note"} />:
                            </b>{" "}
                            {item.note}
                        </td>
                    </tr>
                );
            }

            let totalQty = Number(item.total_qty);

            if (supplierModel && supplierModel.has_order_bonus) {
                totalQty += Number(item.total_bonus_qty_frozen);
            }

            groupTotalQty += totalQty;

            return (
                <tbody key={item.guid}>
                    <tr>
                        <td>{totalQty}</td>
                        <td>{item.Catalog_Item?.sku}</td>
                        <td>{item.Catalog_Item?.name}</td>
                        <td>{item.Catalog_Item?.description}</td>
                        <td>
                            {template === "STANDARD" ? (
                                <div>
                                    <OwLabel title={"qty_picked"} />:
                                </div>
                            ) : null}
                        </td>
                    </tr>
                    {extendedDetails}
                </tbody>
            );
        });

        const groupOrdersDetail = _.map(data.orders, (order) => {
            return (
                <div key={order.guid} className={"col-3"}>
                    <i className={"far fa-square"} /> {order?.name} ({order?.vendor})
                </div>
            );
        });

        return (
            <>
                <div className={printOrders ? "" : "d-print-none"}>
                    <OwTitle className={"mt-3"} title={"orders"} tag={"h5"} />
                    <div className={"row d-flex"}>{groupOrdersDetail}</div>
                </div>
                <div className={"table-responsive"}>
                    <table className={"orders-table table " + tableTheme}>
                        <thead>
                            <tr>
                                <th>
                                    <OwLabel title={"qty"} rowHasSort={true} />
                                </th>
                                <th>
                                    <OwLabel title={"sku"} rowHasSort={true} />
                                </th>
                                <th>
                                    <OwLabel title={"name"} rowHasSort={true} />
                                </th>
                                <th>
                                    <OwLabel title={"description"} rowHasSort={true} />
                                </th>
                                <th>
                                    <OwLabel
                                        className={"no-wrap"}
                                        title={template === "STANDARD" ? "control" : "qty_picked"}
                                        rowHasSort={true}
                                    />
                                </th>
                            </tr>
                        </thead>
                        {groupTbodyRows}
                        <tbody>
                            <tr>
                                <td colSpan={5}>
                                    {groupTotalQty} <OwLabel title={"total"} noBottomMargin={true} />
                                </td>
                            </tr>
                        </tbody>
                        <tfoot />
                    </table>
                </div>
            </>
        );
    };

    const defaultRenderer = () => {
        // Grouped daily routes mode
        if (hasDailyRoutes && groupedPickListData.size > 0) {
            return (
                <div className={"SimplePickList " + template}>
                    <div className={"mt-2 mb-2 d-flex justify-content-between align-items-center flex-wrap"}>
                        <div className={"d-inline-flex"}>
                            <Breadcrumbs hasTopMargin={false} navItems={navItems} />
                        </div>
                        <div className={"d-print-none"}>
                            <OwPrintButton />
                            <div className={"ms-auto w-10r"}>
                                <OwSelectOption
                                    valueProp={"value"}
                                    titleProp={"label"}
                                    fn={selectTemplate}
                                    name={"template"}
                                    options={packingListTemplates}
                                    emitObject={true}
                                    selectedOption={template}
                                    hasSelectedOptionProp={false}
                                />
                            </div>
                        </div>
                    </div>
                    <div className={"d-print-none"}>
                        <OwButton title={"view_packing_lists"} fn={viewPackingLists} className={"btn-primary ms-2"} />
                        <OwButton
                            leftIconClass={"fad fa-download"}
                            title={"export_to_csv"}
                            fn={exportPickListCSV}
                            className={"btn-primary ms-2"}
                        />
                        <div className={"mt-2"}>
                            <OwCheckbox
                                fn={() => {
                                    setPrintOrders(!printOrders);
                                }}
                                checked={printOrders}
                                label={"print_orders_detail"}
                            />
                        </div>
                    </div>
                    {dailyRouteGroups.map((group) => {
                        const groupData = groupedPickListData.get(group.dailyRoute.guid);
                        if (!groupData) return null;

                        return (
                            <div key={group.dailyRoute.guid} className={"PickListGroup"}>
                                <DailyRouteCoverSheet
                                    dailyRouteName={group.dailyRoute.name || ""}
                                    routeName={group.dailyRoute.Route?.name || ""}
                                    routeDate={moment(group.dailyRoute.route_date).format("MM/DD/YYYY")}
                                    driverName={group.dailyRoute.Driver?.name || "-"}
                                    vehicleName={group.dailyRoute.Vehicle?.name || "-"}
                                    orderCount={group.orderGuids.length}
                                />
                                <div>
                                    <OwTitle title={"pick_list"} />
                                    {renderPickListTable(groupData)}
                                </div>
                            </div>
                        );
                    })}
                </div>
            );
        }

        // Standard single pick list mode
        return (
            <div className={"SimplePickList " + template}>
                <div className={"mt-2 mb-2 d-flex justify-content-between align-items-center flex-wrap"}>
                    <div className={"d-inline-flex"}>
                        <Breadcrumbs hasTopMargin={false} navItems={navItems} />
                    </div>
                    <div className={"d-print-none"}>
                        <OwPrintButton />
                        <div className={"ms-auto w-10r"}>
                            <OwSelectOption
                                valueProp={"value"}
                                titleProp={"label"}
                                fn={selectTemplate}
                                name={"template"}
                                options={packingListTemplates}
                                emitObject={true}
                                selectedOption={template}
                                hasSelectedOptionProp={false}
                            />
                        </div>
                    </div>
                </div>
                <div>
                    <OwTitle title={"pick_list"} />
                    <OwButton
                        leftIconClass={"fad fa-download"}
                        title={"export_to_csv"}
                        fn={exportPickListCSV}
                        className={"btn-primary d-print-none"}
                    />
                    <OwButton title={"view_packing_lists"} fn={viewPackingLists} className={"btn-primary ms-2 d-print-none"} />
                    {orders}
                    <div className={"table-responsive"}>
                        <table className={"orders-table table " + tableTheme}>
                            <thead>
                                <tr>
                                    <th>
                                        <OwLabel title={"qty"} rowHasSort={true} />
                                    </th>
                                    <th>
                                        <OwSortHeader
                                            title={"sku"}
                                            currentSortCol={sortCol}
                                            currentSortOrder={sortOrder}
                                            sortFn={sortPickList}
                                        />
                                    </th>
                                    <th>
                                        <OwLabel title={"name"} rowHasSort={true} />
                                    </th>
                                    <th>
                                        <OwLabel title={"description"} rowHasSort={true} />
                                    </th>
                                    <th>
                                        <OwLabel
                                            className={"no-wrap"}
                                            title={template === "STANDARD" ? "control" : "qty_picked"}
                                            rowHasSort={true}
                                        />
                                    </th>
                                </tr>
                            </thead>
                            {tbodyRows}
                            <tbody>
                                <tr>
                                    <td colSpan={5}>
                                        {pickTotalQty} <OwLabel title={"total"} noBottomMargin={true} />
                                    </td>
                                </tr>
                            </tbody>
                            <tfoot />
                        </table>
                    </div>
                </div>
            </div>
        );
    };

    const preFormComponent = () => {
        return (
            <div>
                <PreformPickList notifyFn={uiContext.notifyFn} socket={socket} data={pickListData} toggleMenu={uiContext.toggleMenu} />
            </div>
        );
    };

    {
        switch (supplierModel.pick_list_component) {
            case PickListComponentEnum.PREFORM:
                return preFormComponent();

            default:
                return defaultRenderer();
        }
    }
};
